import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@b2b/ui-components';
import { Activity, User, Clock } from 'lucide-react';
import { rfqService } from '@b2b/services';
import { RfqActivity } from '@b2b/services/api/modules/rfq/rfq.types';
import { formatDistanceToNow } from 'date-fns';

interface RFQActivityTimelineProps {
  rfqId: string;
}

const getActivityLabel = (actionType: string): string => {
  const labels: Record<string, string> = {
    CREATED: 'RFQ Created',
    UPDATED: 'RFQ Updated',
    QUOTE_SUBMITTED: 'Quote Submitted',
    QUOTE_REVISED: 'Quote Revised',
    QUOTE_ACCEPTED: 'Quote Accepted',
    QUOTE_WITHDRAWN: 'Quote Withdrawn',
    RFQ_CANCELLED: 'RFQ Cancelled',
    RFQ_CLOSED: 'RFQ Closed',
    RFQ_EXPIRED: 'RFQ Expired',
    MESSAGE_POSTED: 'Message Posted',
    DISPUTE_RAISED: 'Dispute Raised',
    DISPUTE_RESOLVED: 'Dispute Resolved',
  };
  return labels[actionType] || actionType;
};

const getActivityColor = (actionType: string): string => {
  if (actionType.includes('CREATED') || actionType.includes('SUBMITTED')) {
    return 'bg-blue-100 text-blue-800';
  }
  if (actionType.includes('ACCEPTED') || actionType.includes('RESOLVED')) {
    return 'bg-green-100 text-green-800';
  }
  if (actionType.includes('CANCELLED') || actionType.includes('WITHDRAWN') || actionType.includes('EXPIRED')) {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-gray-100 text-gray-800';
};

export const RFQActivityTimeline: React.FC<RFQActivityTimelineProps> = ({ rfqId }) => {
  const [activities, setActivities] = useState<RfqActivity[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchActivities();
  }, [rfqId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await rfqService.getRfqActivity(rfqId, { page: 1, limit: 50 });
      setActivities(response.items || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Loading activity...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Activity Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <p className="text-muted-foreground text-sm">No activity yet</p>
        ) : (
          <div className="space-y-4">
            {activities.map((activity, index) => (
              <div
                key={activity.id}
                className="flex gap-4 pb-4 last:pb-0 border-l-2 border-muted pl-4"
              >
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={getActivityColor(activity.actionType)}>
                      {getActivityLabel(activity.actionType)}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  {activity.actor && (
                    <p className="text-sm text-muted-foreground">
                      by {activity.actor.firstName} {activity.actor.lastName} ({activity.actorRole})
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

